import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Bell, Plus, Trash2, Eye, Loader2, ArrowLeft, Search, 
  MapPin, DollarSign, Package, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ListingCard from '../components/ListingCard';
import CreateAlertModal from '../components/CreateAlertModal';
import AlertMatchesModal from '../components/AlertMatchesModal';
import { devLog, devError, devWarn } from '../utils/logger';

const conditions = [
  { id: '', name: 'Any Condition' },
  { id: 'excellent', name: 'Excellent' },
  { id: 'good', name: 'Good' },
  { id: 'fair', name: 'Fair' },
  { id: 'salvage', name: 'Salvage' }
];

export default function AlertsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [matchesModal, setMatchesModal] = useState({ open: false, alert: null, matches: [] });
  const [creating, setCreating] = useState(false);
  
  const [newAlert, setNewAlert] = useState({
    category: '',
    keywords: '',
    condition: '',
    max_price: '',
    location: '',
    notify_email: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alertsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/alerts`),
          axios.get(`${API}/listings/categories`)
        ]);
        setAlerts(alertsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        devError('Error fetching alerts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleCreateAlert = async () => {
    if (!newAlert.category) {
      toast.error('Please select a category');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...newAlert,
        max_price: newAlert.max_price ? parseFloat(newAlert.max_price) : null,
        condition: newAlert.condition || null,
        keywords: newAlert.keywords || null,
        location: newAlert.location || null
      };

      const res = await axios.post(`${API}/alerts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlerts([res.data, ...alerts]);
      setCreateModalOpen(false);
      setNewAlert({
        category: '',
        keywords: '',
        condition: '',
        max_price: '',
        location: '',
        notify_email: true
      });
      toast.success('Material alert created! You\'ll be notified when matching items are listed.');
    } catch (error) {
      toast.error('Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleToggleAlert = async (alert) => {
    try {
      await axios.put(
        `${API}/alerts/${alert.alert_id}`,
        { is_active: !alert.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlerts(alerts.map(a => 
        a.alert_id === alert.alert_id ? { ...a, is_active: !a.is_active } : a
      ));
      toast.success(alert.is_active ? 'Alert paused' : 'Alert activated');
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const handleViewMatches = async (alert) => {
    try {
      const res = await axios.get(`${API}/alerts/matches/${alert.alert_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchesModal({ open: true, alert: res.data.alert, matches: res.data.matches });
    } catch (error) {
      toast.error('Failed to load matches');
    }
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : id;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="alerts-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Material Alerts</p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground">
              Your Alerts
            </h1>
            <p className="text-muted-foreground mt-2">
              Get notified when materials matching your criteria are listed.
            </p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="btn-gold"
            data-testid="create-alert-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Alert
          </Button>
        </div>

        {/* Alerts List */}
        {alerts.length === 0 ? (
          <div className="text-center py-16 bg-card/50 rounded-lg border border-border/20">
            <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No alerts yet. Create one to get notified!</p>
            <Button onClick={() => setCreateModalOpen(true)} className="btn-gold">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Alert
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="alerts-list">
            {alerts.map((alert) => (
              <div 
                key={alert.alert_id}
                className={`p-6 rounded-lg bg-card/50 border transition-all ${
                  alert.is_active ? 'border-border/20' : 'border-border/10 opacity-60'
                }`}
                data-testid={`alert-${alert.alert_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Bell className={`w-5 h-5 ${alert.is_active ? 'text-accent' : 'text-muted-foreground'}`} />
                      <h3 className="font-semibold text-lg text-foreground">
                        {getCategoryName(alert.category)}
                      </h3>
                      {alert.matches_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent/20 text-accent">
                          {alert.matches_count} matches
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      {alert.keywords && (
                        <span className="flex items-center gap-1">
                          <Search className="w-3 h-3" />
                          "{alert.keywords}"
                        </span>
                      )}
                      {alert.condition && (
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {alert.condition}
                        </span>
                      )}
                      {alert.max_price && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Max ${alert.max_price}
                        </span>
                      )}
                      {alert.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {alert.location}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {alert.matches_count > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewMatches(alert)}
                        className="border-accent/40 text-accent"
                        data-testid={`view-matches-${alert.alert_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    )}
                    <button
                      onClick={() => handleToggleAlert(alert)}
                      className="p-2 hover:bg-secondary/50 rounded-md transition-colors"
                      title={alert.is_active ? 'Pause alert' : 'Activate alert'}
                    >
                      {alert.is_active ? (
                        <ToggleRight className="w-5 h-5 text-accent" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteAlert(alert.alert_id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                      data-testid={`delete-alert-${alert.alert_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      <CreateAlertModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        newAlert={newAlert}
        setNewAlert={setNewAlert}
        categories={categories}
        creating={creating}
        onCreate={handleCreateAlert}
      />

      {/* View Matches Modal */}
      <AlertMatchesModal
        open={matchesModal.open}
        onOpenChange={(open) => setMatchesModal({ ...matchesModal, open })}
        matches={matchesModal.matches}
      />
    </div>
  );
}
